import { useParams } from "react-router-dom";
import CustomForm from '../../../core/components/core/Util/Form/CustomForm/CustomForm';
import CustomFormContainer from '../../../core/components/core/Util/Form/CustomForm/CustomFormContainer';
import config from '../../config/config';
import Fetch from '../../../core/util/core/Fetch';
import { useEffect, useState } from 'react';
import { chooseProfileFilter } from "../../helpers/chooseProfileFilter";
import sleep from "sleep-promise";

function Form() {
    const profileFilter = chooseProfileFilter();

    const hiddenValues = {};

    if (profileFilter.gci_company_id) {
        hiddenValues.gci_company_id = profileFilter.gci_company_id;
    }

    const getOrganizationType = async (values) => {
        if (profileFilter.organization_type) {
            return profileFilter.organization_type;
        }
        const companyId = values?.gci_company_id;
        if (!companyId) return 'default';
        const company = await Fetch.post(`${config.endpoints.backend}gci/companies/getById`, { id: companyId });
        return company?.organization_type || 'default';
    };

    const getDocumentNumberRequired = async (values) => {
        const documentTypeId = values?.gci_revenue_document_type_id;
        if (!documentTypeId) return false;
        const documentType = await Fetch.post(`${config.endpoints.backend}gci/revenues-document-types/getById`, { id: documentTypeId });
        return documentType?.need_document_number === '1';
    };

    const { id } = useParams();
    const [form, setForm] = useState();
    const [formContent, setFormContent] = useState(<>Carregando...</>);

    const endpointBackend = config.endpoints.backend;

    const paths = {
        endpointSave: `${endpointBackend}gci/expenses/save`,
        endpointGetAllExpenseTypes: `${endpointBackend}gci/expense-types/getall`,
        endpointGetAllAccountingPlans: `${endpointBackend}gci/accounting-plans/getall`,
        endpointGetAllDocumentTypes: `${endpointBackend}gci/revenues-document-types/getall`,
        endpointGetByIdDocumentType: `${endpointBackend}gci/revenues-document-types/getById`,
        endpointGetAllSuppliers: `${endpointBackend}gci/suppliers/getall`,
        endpointGetAllPaymentMethods: `${endpointBackend}gci/payment-methods/getall`,
        endpointGetAllCompanies: `${endpointBackend}gci/companies/getall`,
        endpointGetAllVentures: `${endpointBackend}gci/ventures/getall`,
        endpointGetAllProjects: `${endpointBackend}gci/projects/getall`,
        endpointGetById: `${endpointBackend}gci/expenses/getById`,
        urlGoBack: `/gci/expenses/list`,
        pathView: `/gci/expenses/view`
    };

    const labels = {
        labelTitleAdd: "Nova Despesa",
        labelTitleEdit: "Editar Despesa",
        labelTitleView: 'Visualizar Despesa'
    };

    const rerender = async (values, organizationType, documentNumberRequired) => {
        const form = getForm(values, organizationType, documentNumberRequired);
        form.values = values;
        setForm(form);
        setFormContent(<></>);
        await sleep(1);
        setFormContent(
            <CustomFormContainer
                form={form}
                content={<CustomForm form={form} />}
            />
        );
    };

    const getForm = (values, organizationType = 'default', documentNumberRequired = false) => {
        const isDefaultOrganizationType = organizationType === 'default';

        const form = {
            hiddenValues,
            fields: [
                !profileFilter.gci_company_id ? {
                    name: 'gci_company_id',
                    type: 'select',
                    label: 'Empresa',
                    options: [],
                    optionsEndpoint: {
                        endpoint: paths.endpointGetAllCompanies,
                        label: 'name',
                        value: 'id'
                    },
                    validations: { required: true },
                    handleChange: async (element, field, values) => {
                        const orgType = await getOrganizationType(values);
                        const docNumRequired = await getDocumentNumberRequired(values);
                        await rerender(values, orgType, docNumRequired);
                    },
                    columnSize: 12
                } : {},
                isDefaultOrganizationType ? {
                    name: 'gci_venture_id',
                    type: 'select',
                    label: 'Empreendimento',
                    options: [],
                    optionsEndpoint: {
                        endpoint: paths.endpointGetAllVentures,
                        label: 'enterprise_name',
                        value: 'id',
                        params: {
                            filter: {
                                ...profileFilter,
                                gci_company_id: profileFilter.gci_company_id || values.gci_company_id || null,
                            }
                        }
                    },
                    validations: { required: true },
                    columnSize: 12,
                    handleChange: async (element, field, values) => {
                        const orgType = await getOrganizationType(values);
                        const docNumRequired = await getDocumentNumberRequired(values);
                        await rerender(values, orgType, docNumRequired);
                    },
                } : {
                    name: 'gci_project_id',
                    type: 'select',
                    label: 'Projeto',
                    options: [],
                    optionsEndpoint: {
                        endpoint: paths.endpointGetAllProjects,
                        label: 'project_name',
                        value: 'id',
                        params: {
                            filter: {
                                gci_company_id: profileFilter.gci_company_id || values.gci_company_id || null,
                            }
                        }
                    },
                    validations: { required: true },
                    columnSize: 12,
                    handleChange: async (element, field, values) => {
                        const orgType = await getOrganizationType(values);
                        const docNumRequired = await getDocumentNumberRequired(values);
                        await rerender(values, orgType, docNumRequired);
                    }
                },
                {
                    name: 'gci_expense_type_id',
                    type: 'select',
                    label: 'Tipo de Despesa',
                    options: [],
                    optionsEndpoint: {
                        endpoint: paths.endpointGetAllExpenseTypes,
                        label: 'name',
                        value: 'id',
                        params: { filter: { ...profileFilter } }
                    },
                    validations: { required: true },
                    columnSize: 4
                },
                {
                    name: 'gci_accounting_plan_id',
                    type: 'select',
                    label: 'Plano de Contas',
                    options: [],
                    optionsEndpoint: {
                        endpoint: paths.endpointGetAllAccountingPlans,
                        label: 'full_path',
                        value: 'id',
                        params: {
                            filter: {
                                ...profileFilter,
                                gci_company_id: profileFilter.gci_company_id || values.gci_company_id || null,
                                onlyLastLevel: true
                            }
                        }
                    },
                    validations: { required: true },
                    columnSize: 8
                },
                {
                    name: 'gci_revenue_document_type_id',
                    type: 'select',
                    label: 'Tipo Doc',
                    options: [],
                    optionsEndpoint: {
                        endpoint: paths.endpointGetAllDocumentTypes,
                        label: 'name',
                        value: 'id',
                    },
                    validations: { required: false },
                    columnSize: 4,
                    handleChange: async (element, field, values) => {
                        const orgType = await getOrganizationType(values);
                        const docNumRequired = await getDocumentNumberRequired(values);
                        await rerender(values, orgType, docNumRequired);
                    }
                },
                {
                    name: 'document_number',
                    type: 'text',
                    label: 'Número do Doc',
                    validations: { required: documentNumberRequired },
                    columnSize: 4
                },
                {
                    name: 'gci_supplier_id',
                    type: 'select',
                    label: 'Fornecedor',
                    options: [],
                    optionsEndpoint: {
                        endpoint: paths.endpointGetAllSuppliers,
                        label: 'supplier_name',
                        value: 'id',
                        params: {
                            filter: {
                                gci_company_id: profileFilter.gci_company_id || values.gci_company_id || null,
                            }
                        }
                    },
                    validations: { required: true },
                    columnSize: 4
                },
                {
                    name: 'description',
                    type: 'text',
                    label: 'Descrição',
                    validations: { required: false },
                    columnSize: 12
                },
                {
                    name: 'estimated_value',
                    type: 'decimal',
                    label: 'Valor Estimado',
                    validations: { required: false },
                    columnSize: 6
                },
                {
                    name: 'value',
                    type: 'decimal',
                    label: 'Valor da Despesa',
                    validations: { required: true },
                    columnSize: 6
                },
                {
                    name: 'emission_date',
                    type: 'date',
                    label: 'Data de Emissão',
                    validations: { required: true },
                    columnSize: 6
                },
                
                {
                    name: 'due_date',
                    type: 'date',
                    label: 'Data de Vencimento',
                    validations: { required: true },
                    columnSize: 6
                },
                {
                    name: 'gci_payment_method_id',
                    type: 'select',
                    label: 'Forma de Pagamento',
                    options: [],
                    optionsEndpoint: {
                        endpoint: paths.endpointGetAllPaymentMethods,
                        label: 'name',
                        value: 'id',
                        params: {
                            filter: {
                                type: ['expense', 'both']
                            }
                        }
                    },
                    validations: { required: false },
                    columnSize: 12
                },
                {
                    name: 'observation',
                    type: 'textarea',
                    label: 'Observação',
                    validations: { required: false },
                    columnSize: 12
                },
                id ? {
                    name: 'formattedStatus',
                    type: 'text',
                    label: 'Status',
                    validations: { required: false },
                    readOnly: true,
                    columnSize: 6
                } : {},
                id ? {
                    name: 'origin',
                    type: 'text',
                    label: 'Origem',
                    validations: { required: false },
                    readOnly: true,
                    columnSize: 6
                } : {},
            ],
            ...paths,
            ...labels,
        };
        form.values = values;
        return form;
    };

    useEffect(() => {
        const load = async () => {
            let values = {};
            if (id) {
                values = await Fetch.post(paths.endpointGetById, { id });
            } else {
                values.emission_date = new Date();
                values.due_date = new Date();
                values.origin = 'manual';
            }

            const organizationType = await getOrganizationType(values);
            const documentNumberRequired = await getDocumentNumberRequired(values);

            const form = getForm(values, organizationType, documentNumberRequired);

            setForm(form);
            setFormContent(
                <CustomFormContainer
                    form={form}
                    content={<CustomForm form={form} />} />
            );
        };
        load();
    }, []);

    return (
        <div>
            {formContent}
        </div>
    );
}

export default Form;
